import React, { useEffect, useMemo, useRef, useState } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import Swal from "sweetalert2";
import { showModal, hideModal, installModalFallback } from "./Modalhelper";
import { useFormik } from "formik";
import * as Yup from "yup";
import "./Folders.css";

const baseUrl = process.env.REACT_APP_BASE_URL;

// Routes de FolderController et DocumentController
const API = {
    folders: `${baseUrl}/api/ged/folders`,
    folderById: (id) => `${baseUrl}/api/ged/folders/${id}`,
    renameFolder: (id) => `${baseUrl}/api/ged/folders/${id}/rename`,
    documentsByFolder: (folderId) => `${baseUrl}/api/ged/documents/folder/${folderId}`,
    uploadDocument: `${baseUrl}/api/ged/documents/upload`,
    downloadDocument: (id) => `${baseUrl}/api/ged/documents/${id}/download`,
    previewDocument: (id) => `${baseUrl}/api/ged/documents/${id}/preview`,
    deleteDocument: (id) => `${baseUrl}/api/ged/documents/${id}`,
};

// Les réponses de l'API sont enveloppées dans un ServiceResponse { success, statusCode, data, message }
const unwrap = (res) => (res.data && res.data.data !== undefined ? res.data.data : res.data);
const apiMessage = (error, fallback) =>
    error.response?.data?.message || error.response?.data?.Message || fallback;

const toast = (icon, title) =>
    Swal.fire({ toast: true, position: "top-end", icon, title, showConfirmButton: false, timer: 2000 });

// Type de média d'un document (pour la galerie)
const mediaKind = (d) => {
    const ct = (d.contentType || "").toLowerCase();
    const ext = (d.fileName || "").split(".").pop().toLowerCase();
    if (ct.startsWith("image/") || ["jpg", "jpeg", "png", "gif", "webp", "bmp"].includes(ext)) return "image";
    if (ct.startsWith("video/") || ["mp4", "webm", "mov", "avi", "mkv"].includes(ext)) return "video";
    return null;
};

const validationSchema = Yup.object({
    name: Yup.string().trim().required("Le nom est obligatoire").max(255, "255 caractères maximum"),
    description: Yup.string().nullable(),
});

// Construit l'arbre à partir de la liste plate (Id / ParentId)
const buildTree = (folders) => {
    const map = {};
    folders.forEach((f) => (map[f.id] = { ...f, children: [] }));
    const roots = [];
    folders.forEach((f) => {
        if (f.parentId && map[f.parentId]) map[f.parentId].children.push(map[f.id]);
        else roots.push(map[f.id]);
    });
    const sort = (nodes) => {
        nodes.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name));
        nodes.forEach((n) => sort(n.children));
    };
    sort(roots);
    return roots;
};

const FolderNode = ({ node, selectedId, expanded, onToggle, onSelect }) => {
    const isOpen = expanded.has(node.id);
    const hasChildren = node.children.length > 0;
    return (
        <li>
            <div
                className={`folder-node ${selectedId === node.id ? "active" : ""}`}
                onClick={() => onSelect(node.id)}
                title={node.name}
            >
                <span
                    className="folder-toggle"
                    onClick={(e) => {
                        e.stopPropagation();
                        if (hasChildren) onToggle(node.id);
                    }}
                >
                    {hasChildren ? <i className={`fas fa-caret-${isOpen ? "down" : "right"}`}></i> : null}
                </span>
                <i className={`fas fa-folder${isOpen ? "-open" : ""} text-warning mr-2`}></i>
                <span className="folder-node-name">{node.name}</span>
            </div>
            {hasChildren && isOpen && (
                <ul className="folder-tree">
                    {node.children.map((c) => (
                        <FolderNode key={c.id} node={c} selectedId={selectedId}
                            expanded={expanded} onToggle={onToggle} onSelect={onSelect} />
                    ))}
                </ul>
            )}
        </li>
    );
};

const Folders = () => {
    const [folders, setFolders] = useState([]);
    const [documents, setDocuments] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [expanded, setExpanded] = useState(new Set());
    const [editingFolder, setEditingFolder] = useState(null); // null = création
    const [loading, setLoading] = useState(true);

    // Vue Liste / Galerie
    const [view, setView] = useState("list");
    const [thumbs, setThumbs] = useState({});                  // { [docId]: objectURL }
    const thumbsRef = useRef({});
    const [lightbox, setLightbox] = useState(null);            // { doc, url, kind, loading }

    const tree = useMemo(() => buildTree(folders), [folders]);
    const selectedFolder = folders.find((f) => f.id === selectedId) || null;
    const subFolders = folders.filter((f) => (f.parentId || null) === selectedId);
    const mediaDocs = useMemo(() => documents.filter((d) => mediaKind(d)), [documents]);

    // Fil d'Ariane : remonte les parents
    const breadcrumb = useMemo(() => {
        const path = [];
        let current = selectedFolder;
        while (current) {
            path.unshift(current);
            current = folders.find((f) => f.id === current.parentId);
        }
        return path;
    }, [selectedFolder, folders]);

    useEffect(() => {
        fetchFolders();
    }, []);

    // Popups utilisables même sans jQuery / Bootstrap JS
    useEffect(() => installModalFallback(), []);

    useEffect(() => {
        if (selectedId) fetchDocuments(selectedId);
        else setDocuments([]);
        clearThumbs();
        setView("list");
    }, [selectedId]);


    // Charge les miniatures des photos en vue Galerie
    useEffect(() => {
        if (view !== "gallery") return;
        mediaDocs
            .filter((d) => mediaKind(d) === "image" && !thumbsRef.current[d.id])
            .forEach(async (d) => {
                try {
                    const res = await axiosInstance.get(API.previewDocument(d.id), { responseType: "blob" });
                    const url = window.URL.createObjectURL(res.data);
                    thumbsRef.current = { ...thumbsRef.current, [d.id]: url };
                    setThumbs({ ...thumbsRef.current });
                } catch {
                    /* miniature indisponible : on garde l'icône */
                }
            });
    }, [view, mediaDocs]);

    // Libère la mémoire des miniatures en quittant la page
    useEffect(() => () => clearThumbs(), []);

    const clearThumbs = () => {
        Object.values(thumbsRef.current).forEach((u) => window.URL.revokeObjectURL(u));
        thumbsRef.current = {};
        setThumbs({});
    };

    const fetchFolders = () => {
        axiosInstance.get(API.folders)
            .then((res) => setFolders(unwrap(res) || []))
            .catch((error) => toast("error", apiMessage(error, "Impossible de charger les dossiers")))
            .finally(() => setLoading(false));
    };

    const fetchDocuments = (folderId) => {
        axiosInstance.get(API.documentsByFolder(folderId))
            .then((res) => setDocuments(unwrap(res) || []))
            .catch(() => setDocuments([]));
    };

    const selectFolder = (id) => {
        setSelectedId(id);
        // déplie automatiquement les parents
        const next = new Set(expanded);
        let current = folders.find((f) => f.id === id);
        while (current?.parentId) {
            next.add(current.parentId);
            current = folders.find((f) => f.id === current.parentId);
        }
        setExpanded(next);
    };

    const toggle = (id) => {
        const next = new Set(expanded);
        next.has(id) ? next.delete(id) : next.add(id);
        setExpanded(next);
    };

    // ---------- Création / modification de dossier ----------
    const formik = useFormik({
        initialValues: { name: "", description: "" },
        validationSchema,
        onSubmit: async (values, { setErrors, resetForm }) => {
            try {
                if (editingFolder) {
                    if (values.name !== editingFolder.name) {
                        await axiosInstance.put(API.renameFolder(editingFolder.id), { name: values.name });
                    }
                    if ((values.description || "") !== (editingFolder.description || "")) {
                        await axiosInstance.put(API.folderById(editingFolder.id), {
                            name: values.name,
                            description: values.description || null,
                        });
                    }
                } else {
                    await axiosInstance.post(API.folders, {
                        name: values.name,
                        description: values.description || null,
                        parentId: selectedId, // null = dossier racine
                    });
                    if (selectedId) setExpanded(new Set(expanded).add(selectedId));
                }

                hideModal("folderModal");
                toast("success", "Enregistrement effectué avec succès");
                resetForm();
                setEditingFolder(null);
                fetchFolders();
            } catch (error) {
                if (error.response?.data?.errors) setErrors(error.response.data.errors);
                else toast("error", apiMessage(error, "Opération échouée"));
            }
        },
    });

    const openCreate = () => {
        setEditingFolder(null);
        formik.resetForm();
        showModal("folderModal");
    };

    const openEdit = (folder) => {
        setEditingFolder(folder);
        formik.setValues({ name: folder.name || "", description: folder.description || "" });
        showModal("folderModal");
    };

    const handleDelete = (folder) => {
        Swal.fire({
            title: `Supprimer « ${folder.name} » ?`,
            text: "Le dossier doit être vide.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Oui, supprimer",
            cancelButtonText: "Annuler",
        }).then(async (result) => {
            if (!result.isConfirmed) return;
            try {
                await axiosInstance.delete(API.folderById(folder.id));
                toast("success", "Dossier supprimé");
                if (selectedId === folder.id) setSelectedId(folder.parentId || null);
                fetchFolders();
            } catch (error) {
                const message = error.response?.status === 409
                    ? apiMessage(error, "Le dossier n'est pas vide.")
                    : apiMessage(error, "Suppression échouée");
                Swal.fire({ icon: "error", title: "Impossible de supprimer", text: message });
            }
        });
    };

    // ---------- Documents ----------
    const handleUpload = async (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = ""; // permet de re-sélectionner le même fichier
        if (!selectedId || files.length === 0) return;

        try {
            for (const file of files) {
                const formData = new FormData();
                formData.append("file", file);
                formData.append("folderId", selectedId);
                await axiosInstance.post(API.uploadDocument, formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
            }
            toast("success", files.length > 1 ? `${files.length} fichiers ajoutés` : "Fichier ajouté");
            fetchDocuments(selectedId);
        } catch (error) {
            Swal.fire({ icon: "error", title: "Upload échoué", text: apiMessage(error, "Erreur lors de l'envoi") });
            fetchDocuments(selectedId);
        }
    };

    const handleDeleteDocument = (doc) => {
        Swal.fire({
            title: `Supprimer « ${doc.fileName} » ?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Oui, supprimer",
            cancelButtonText: "Annuler",
        }).then(async (result) => {
            if (!result.isConfirmed) return;
            try {
                await axiosInstance.delete(API.deleteDocument(doc.id));
                toast("success", "Document supprimé");
                if (lightbox?.doc.id === doc.id) closeLightbox();
                fetchDocuments(selectedId);
            } catch (error) {
                toast("error", apiMessage(error, "Suppression échouée"));
            }
        });
    };

    const handleDownload = async (doc) => {
        try {
            const res = await axiosInstance.get(API.downloadDocument(doc.id), { responseType: "blob" });
            const url = window.URL.createObjectURL(res.data);
            const link = document.createElement("a");
            link.href = url;
            link.download = doc.fileName || "document";
            link.click();
            window.URL.revokeObjectURL(url);
        } catch {
            toast("error", "Téléchargement échoué");
        }
    };

    // ---------- Visionneuse plein écran ----------
    const openLightbox = async (doc) => {
        const kind = mediaKind(doc);
        if (kind === "image" && thumbs[doc.id]) {
            setLightbox({ doc, kind, url: thumbs[doc.id], owned: false });
            return;
        }
        setLightbox({ doc, kind, url: null, loading: true });
        try {
            const res = await axiosInstance.get(API.previewDocument(doc.id), { responseType: "blob" });
            setLightbox({ doc, kind, url: window.URL.createObjectURL(res.data), owned: true });
        } catch {
            setLightbox(null);
            toast("error", "Aperçu indisponible");
        }
    };

    const closeLightbox = () => {
        if (lightbox?.owned && lightbox.url) window.URL.revokeObjectURL(lightbox.url);
        setLightbox(null);
    };

    const moveLightbox = (step) => {
        if (!lightbox) return;
        const i = mediaDocs.findIndex((d) => d.id === lightbox.doc.id);
        const next = mediaDocs[(i + step + mediaDocs.length) % mediaDocs.length];
        if (lightbox.owned && lightbox.url) window.URL.revokeObjectURL(lightbox.url);
        openLightbox(next);
    };

    useEffect(() => {
        if (!lightbox) return;
        const onKey = (e) => {
            if (e.key === "Escape") closeLightbox();
            if (e.key === "ArrowRight") moveLightbox(1);
            if (e.key === "ArrowLeft") moveLightbox(-1);
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [lightbox, mediaDocs]);

    // ---------- Rendu : galerie ----------
    const renderGallery = () => (
        <>
            {subFolders.length > 0 && (
                <div className="d-flex flex-wrap mb-3">
                    {subFolders.map((f) => (
                        <button key={f.id} className="btn btn-light btn-sm mr-2 mb-2" onClick={() => selectFolder(f.id)}>
                            <i className="fas fa-folder text-warning mr-1"></i>{f.name}
                        </button>
                    ))}
                </div>
            )}
            {mediaDocs.length === 0 ? (
                <div className="text-center text-muted py-5 bg-white rounded">
                    <i className="far fa-images fa-3x mb-3 d-block"></i>
                    Aucune photo ni vidéo dans ce dossier.
                </div>
            ) : (
                <div className="media-grid">
                    {mediaDocs.map((d) => {
                        const kind = mediaKind(d);
                        return (
                            <div key={d.id} className="media-tile" onClick={() => openLightbox(d)} title={d.fileName}>
                                {kind === "image" && thumbs[d.id] ? (
                                    <img src={thumbs[d.id]} alt={d.fileName} loading="lazy" />
                                ) : (
                                    <div className="media-placeholder">
                                        <i className={`fas ${kind === "video" ? "fa-play-circle" : "fa-image"} fa-3x`}></i>
                                    </div>
                                )}
                                {kind === "video" && <span className="media-badge"><i className="fas fa-video"></i></span>}
                                <div className="media-caption">{d.fileName}</div>
                            </div>
                        );
                    })}
                </div>
            )}
            {documents.length > mediaDocs.length && (
                <p className="small text-muted mt-2">
                    {documents.length - mediaDocs.length} autre(s) fichier(s) (non photo/vidéo) : passez en vue Liste pour les voir.
                </p>
            )}
        </>
    );

    // ---------- Rendu : liste ----------
    const renderList = () => (
        <table className="table table-hover table-sm bg-white">
            <thead className="thead-light">
                <tr>
                    <th>Nom</th>
                    <th>Type</th>
                    <th>Créé le</th>
                    <th className="text-right">Actions</th>
                </tr>
            </thead>
            <tbody>
                {subFolders.map((f) => {
                    return (
                        <tr key={`f-${f.id}`} className="folder-row" onDoubleClick={() => selectFolder(f.id)}>
                            <td>
                                <i className="fas fa-folder text-warning mr-2"></i>
                                <a href="#!" onClick={(e) => { e.preventDefault(); selectFolder(f.id); }}>{f.name}</a>
                            </td>
                            <td>Dossier</td>
                            <td>{f.createdAt ? new Date(f.createdAt).toLocaleDateString("fr-FR") : ""}</td>
                            <td className="text-right text-nowrap">
                                <button className="btn btn-sm btn-outline-secondary mr-1" title="Modifier" onClick={() => openEdit(f)}>
                                    <i className="fas fa-edit"></i>
                                </button>
                                <button className="btn btn-sm btn-outline-danger" title="Supprimer" onClick={() => handleDelete(f)}>
                                    <i className="fas fa-trash"></i>
                                </button>
                            </td>
                        </tr>
                    );
                })}
                {documents.map((d) => {
                    const kind = mediaKind(d);
                    return (
                        <tr key={`d-${d.id}`}>
                            <td>
                                <i className={`fas ${kind === "image" ? "fa-file-image text-info" : kind === "video" ? "fa-file-video text-danger" : "fa-file-alt text-secondary"} mr-2`}></i>
                                {kind
                                    ? <a href="#!" onClick={(e) => { e.preventDefault(); openLightbox(d); }}>{d.fileName}</a>
                                    : d.fileName}
                            </td>
                            <td>{kind === "image" ? "Photo" : kind === "video" ? "Vidéo" : "Document"}</td>
                            <td>{(d.createdAt || d.uploadedAt) ? new Date(d.createdAt || d.uploadedAt).toLocaleDateString("fr-FR") : ""}</td>
                            <td className="text-right text-nowrap">
                                <button className="btn btn-sm btn-outline-primary mr-1" title="Télécharger" onClick={() => handleDownload(d)}>
                                    <i className="fas fa-download"></i>
                                </button>
                                <button className="btn btn-sm btn-outline-danger" title="Supprimer" onClick={() => handleDeleteDocument(d)}>
                                    <i className="fas fa-trash"></i>
                                </button>
                            </td>
                        </tr>
                    );
                })}
                {subFolders.length === 0 && documents.length === 0 && (
                    <tr><td colSpan="4" className="text-center text-muted py-4">Ce dossier est vide</td></tr>
                )}
            </tbody>
        </table>
    );

    return (
        <div className="container-fluid mt-4">
            <h3 className="text-center my-3">Gestion Électronique des Documents</h3>

            <div className="row">
                {/* ---- Arbre des dossiers ---- */}
                <div className="col-md-3">
                    <div className="card">
                        <div className="card-header d-flex justify-content-between align-items-center">
                            <strong>Dossiers</strong>
                            <button className="btn btn-sm btn-link" title="Racine" onClick={() => setSelectedId(null)}>
                                <i className="fas fa-home"></i>
                            </button>
                        </div>
                        <div className="card-body folder-tree-container">
                            {loading ? (
                                <p>Chargement...</p>
                            ) : tree.length === 0 ? (
                                <p className="text-muted">Aucun dossier</p>
                            ) : (
                                <ul className="folder-tree root">
                                    {tree.map((n) => (
                                        <FolderNode key={n.id} node={n} selectedId={selectedId}
                                            expanded={expanded} onToggle={toggle} onSelect={selectFolder} />
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                </div>

                {/* ---- Contenu du dossier ---- */}
                <div className="col-md-9">
                    <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
                        <nav>
                            <ol className="breadcrumb mb-0">
                                <li className={`breadcrumb-item ${!selectedId ? "active" : ""}`}>
                                    <a href="#!" onClick={(e) => { e.preventDefault(); setSelectedId(null); }}>Racine</a>
                                </li>
                                {breadcrumb.map((f, i) => (
                                    <li key={f.id} className={`breadcrumb-item ${i === breadcrumb.length - 1 ? "active" : ""}`}>
                                        {i === breadcrumb.length - 1 ? f.name : (
                                            <a href="#!" onClick={(e) => { e.preventDefault(); selectFolder(f.id); }}>{f.name}</a>
                                        )}
                                    </li>
                                ))}
                            </ol>
                        </nav>
                        <div className="d-flex align-items-center">
                            {selectedId && (
                                <div className="btn-group btn-group-sm mr-2">
                                    <button className={`btn ${view === "list" ? "btn-secondary" : "btn-outline-secondary"}`} onClick={() => setView("list")} title="Vue liste">
                                        <i className="fas fa-list"></i>
                                    </button>
                                    <button className={`btn ${view === "gallery" ? "btn-secondary" : "btn-outline-secondary"}`} onClick={() => setView("gallery")} title="Vue galerie">
                                        <i className="fas fa-th"></i>
                                    </button>
                                </div>
                            )}
                            <label
                                className={`btn btn-outline-primary mb-0 mr-2 ${!selectedId ? "disabled" : ""}`}
                                title={!selectedId ? "Sélectionnez d'abord un dossier" : "Ajouter des fichiers"}
                            >
                                <i className="fas fa-upload"></i> Ajouter des documents
                                <input
                                    type="file"
                                    multiple
                                    hidden
                                    disabled={!selectedId}
                                    onChange={handleUpload}
                                />
                            </label>
                            <button className="btn btn-primary btn-ajouter" onClick={openCreate}>
                                <i className="fas fa-folder-plus"></i> Nouveau dossier
                            </button>
                        </div>
                    </div>

                    {view === "gallery" && selectedId ? renderGallery() : renderList()}
                </div>
            </div>

            {/* ---- Visionneuse photo / vidéo ---- */}
            {lightbox && (
                <div className="media-lightbox" onClick={closeLightbox}>
                    <div className="media-lightbox-toolbar" onClick={(e) => e.stopPropagation()}>
                        <span className="text-truncate mr-3">{lightbox.doc.fileName}</span>
                        <div className="text-nowrap">
                            <button className="btn btn-sm btn-light mr-2" onClick={() => handleDownload(lightbox.doc)}>
                                <i className="fas fa-download"></i>
                            </button>
                            <button className="btn btn-sm btn-light mr-2" onClick={() => handleDeleteDocument(lightbox.doc)}>
                                <i className="fas fa-trash text-danger"></i>
                            </button>
                            <button className="btn btn-sm btn-light" onClick={closeLightbox}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                    </div>

                    {mediaDocs.length > 1 && (
                        <>
                            <button className="media-nav media-nav-left" onClick={(e) => { e.stopPropagation(); moveLightbox(-1); }}>
                                <i className="fas fa-chevron-left"></i>
                            </button>
                            <button className="media-nav media-nav-right" onClick={(e) => { e.stopPropagation(); moveLightbox(1); }}>
                                <i className="fas fa-chevron-right"></i>
                            </button>
                        </>
                    )}

                    <div className="media-lightbox-content" onClick={(e) => e.stopPropagation()}>
                        {lightbox.loading || !lightbox.url ? (
                            <i className="fas fa-spinner fa-spin fa-3x text-white"></i>
                        ) : lightbox.kind === "video" ? (
                            <video src={lightbox.url} controls autoPlay />
                        ) : (
                            <img src={lightbox.url} alt={lightbox.doc.fileName} />
                        )}
                    </div>
                </div>
            )}

            {/* ---- Modal création / modification de dossier ---- */}
            <div className="modal fade" id="folderModal">
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5>
                                {editingFolder ? "Modifier le dossier" : "Nouveau dossier"}
                                {!editingFolder && selectedFolder && (
                                    <small className="text-muted"> dans « {selectedFolder.name} »</small>
                                )}
                            </h5>
                            <button type="button" className="close" data-dismiss="modal"><span>&times;</span></button>
                        </div>
                        <div className="modal-body">
                            <div className="form-group">
                                <label>Nom</label>
                                <input
                                    type="text"
                                    name="name"
                                    className={`form-control ${formik.touched.name && formik.errors.name ? "is-invalid" : ""}`}
                                    value={formik.values.name}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                />
                                <div className="invalid-feedback">{formik.errors.name}</div>
                            </div>
                            <div className="form-group">
                                <label>Description</label>
                                <textarea
                                    name="description"
                                    rows={3}
                                    className="form-control"
                                    value={formik.values.description}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                />
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-secondary" data-dismiss="modal">Annuler</button>
                            <button type="button" className="btn btn-enregistrer" onClick={formik.handleSubmit}>Sauvegarder</button>
                        </div>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default Folders;
